/**
 * POS-I299: the phone page at /m must be able to offer the install even though the browser fires `beforeinstallprompt` early (right
 * after load), long before the page has opened its session and mounted the install sheet. This module is imported first by main.tsx:
 * on /m it (1) points the manifest link at the phone manifest BEFORE the browser judges installability, so it never judges the admin
 * manifest (start /pos, scope /), and (2) keeps the event so the sheet can use it whenever it appears.
 */
import { manifestHref } from '@/lib/mobile-handover';

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let captured: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(e: BeforeInstallPromptEvent | null) => void>();

export const isMobilePath = (pathname: string): boolean => pathname === '/m' || pathname.startsWith('/m/');

export function getCapturedInstallPrompt(): BeforeInstallPromptEvent | null {
  return captured;
}

export function onInstallPromptChange(fn: (e: BeforeInstallPromptEvent | null) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The sheet used the event (the browser allows one prompt per event) or the app got installed. */
export function clearCapturedInstallPrompt(): void {
  captured = null;
  listeners.forEach((fn) => fn(null));
}

interface CaptureEnv {
  pathname: string;
  search: string;
  hash: string;
  replaceUrl(url: string): void;
  link: { setAttribute(name: string, value: string): void } | null;
  addEventListener(type: string, fn: (e: Event) => void): void;
}

/** Wires the capture to a page (the real window in the browser, a fake in the unit tests). Does nothing outside /m. */
export function installCapture(env: CaptureEnv): void {
  if (!isMobilePath(env.pathname)) return;
  // The phone manifest's scope is /m/, so the page itself must be inside it: /m (what the link and the QR code open) is not, and the
  // browser then says "not installable". Same document, only the address changes; the query and the link token (fragment) are kept.
  if (env.pathname === '/m') env.replaceUrl(`/m/${env.search}${env.hash}`);
  env.link?.setAttribute('href', manifestHref());
  env.link?.setAttribute('crossorigin', 'use-credentials');
  env.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    captured = e as BeforeInstallPromptEvent;
    listeners.forEach((fn) => fn(captured));
  });
  env.addEventListener('appinstalled', clearCapturedInstallPrompt);
}

if (typeof window !== 'undefined') {
  installCapture({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    replaceUrl: (url) => { try { window.history.replaceState(null, '', url); } catch { /* the page still works, only the install offer may not show */ } },
    link: document.querySelector<HTMLLinkElement>('link[rel="manifest"]'),
    addEventListener: (t, fn) => window.addEventListener(t, fn),
  });
}
